import {
  COMIC_PAGE_LAYOUT_DEFAULT,
  type ComicPageLayoutTemplate,
  getPageLayoutOption,
  normalizePageLayout,
  pageHasRoom,
  pageMaxPanels,
} from './comicPageLayout'
import type { ComicEpisode, ComicPage, ComicPanel } from './comicTypes'
import { randomUuid } from './randomUuid'

/** 默认页标题「第 N 页」；删除/重排后需同步上浮序号 */
const DEFAULT_PAGE_TITLE_RE = /^第 \d+ 页$/

export function defaultComicPageTitle(zeroBasedIndex: number): string {
  return `第 ${zeroBasedIndex + 1} 页`
}

/** 保留用户自定义标题；默认标题随 index 重算 */
export function comicPageTitleAtIndex(page: ComicPage, zeroBasedIndex: number): string {
  const t = page.title?.trim() ?? ''
  if (!t || DEFAULT_PAGE_TITLE_RE.test(t)) return defaultComicPageTitle(zeroBasedIndex)
  return t
}

export function reindexComicPages(pages: ComicPage[]): ComicPage[] {
  return pages.map((page, i) => ({
    ...page,
    index: i + 1,
    title: comicPageTitleAtIndex(page, i),
  }))
}

/** 将各页超出 maxPanels 的分镜 id 摘出，页内仅保留版式容量内 */
export function collectOverflowPanelIds(pages: ComicPage[]): {
  pages: ComicPage[]
  spill: string[]
} {
  const spill: string[] = []
  const trimmed = pages.map((page) => {
    const max = pageMaxPanels(page.layout)
    if (page.panelIds.length <= max) return page
    spill.push(...page.panelIds.slice(max))
    return { ...page, panelIds: page.panelIds.slice(0, max) }
  })
  return { pages: trimmed, spill }
}

/** 从 startPageIndex 起向后页填充 spill；剩余返回 spill */
export function distributePanelIdsToPages(
  pages: ComicPage[],
  panelIds: string[],
  startPageIndex = 0,
): { pages: ComicPage[]; spill: string[] } {
  const out = pages.map((p) => ({ ...p, panelIds: [...p.panelIds] }))
  const spill = [...panelIds]
  for (let i = Math.max(0, startPageIndex); i < out.length && spill.length > 0; i++) {
    const page = out[i]!
    const room = pageMaxPanels(page.layout) - page.panelIds.length
    if (room > 0) {
      const take = spill.splice(0, room)
      out[i] = { ...page, panelIds: [...page.panelIds, ...take] }
    }
  }
  return { pages: out, spill }
}

export function createEmptyPage(index: number, layout: ComicPageLayoutTemplate = COMIC_PAGE_LAYOUT_DEFAULT): ComicPage {
  return {
    id: randomUuid(),
    index,
    title: `第 ${index} 页`,
    layout,
    panelIds: [],
    pageImageUrl: null,
    selectedPageCandidateId: null,
    pageCandidates: [],
    pageImageStatus: 'draft',
    pageErrorMessage: undefined,
    pageCreationTaskId: null,
  }
}

/** 仅当无分页或分页未引用任何分镜时才做完整迁移（避免保存后 pageId 被重写导致选中回弹） */
function episodeNeedsPageMigration(episode: ComicEpisode): boolean {
  if (episode.pages.length === 0) return true
  const panelIds = new Set(episode.panels.map((p) => p.id))
  if (panelIds.size === 0) return false
  const referenced = episode.pages.some((page) =>
    page.panelIds.some((id) => panelIds.has(id)),
  )
  return !referenced
}

function pageHasWorkflowStoryboardHints(page: ComicPage): boolean {
  return Boolean(
    page.summary?.trim() ||
      page.storyGoal?.trim() ||
      page.visualPrompt?.trim() ||
      page.dialogueHint?.trim(),
  )
}

/** 工作流创建后可能只有分页骨架、无分镜格；按版式容量补齐 panel 并回填页级脚本 */
export function seedMissingPagePanels(episode: ComicEpisode): ComicEpisode {
  const normalized = ensureEpisodePages(episode)
  const needsSeed = normalized.pages.some(
    (page) => page.panelIds.length === 0 && pageHasWorkflowStoryboardHints(page),
  )
  if (!needsSeed) return normalized

  let order =
    normalized.panels.length > 0 ? Math.max(...normalized.panels.map((p) => p.order)) + 1 : 1
  const panels = [...normalized.panels]
  const pages = normalized.pages.map((page) => {
    if (page.panelIds.length > 0 || !pageHasWorkflowStoryboardHints(page)) return page
    const enriched = enrichPageStoryboardHints(page)
    const max = pageMaxPanels(enriched.layout)
    const scripts = splitPageScript(enriched, max)
    const dialogueHint = enriched.dialogueHint?.trim() ?? ''
    const panelIds: string[] = []
    for (let slot = 0; slot < max; slot++) {
      const panel: ComicPanel = {
        id: randomUuid(),
        order: order++,
        script: scripts[slot] ?? '',
        dialogue: slot === 0 ? dialogueHint : '',
        narration: '',
        imageUrl: null,
        selectedCandidateId: null,
        candidates: [],
        creationTaskId: null,
        status: 'draft',
      }
      panels.push(panel)
      panelIds.push(panel.id)
    }
    return { ...enriched, panelIds }
  })
  return renumberEpisodePanelOrders({ ...normalized, panels, pages })
}

function enrichPageStoryboardHints(page: ComicPage): ComicPage {
  const summary = page.summary?.trim() ?? ''
  const storyGoal = page.storyGoal?.trim() ?? ''
  const visualPrompt = page.visualPrompt?.trim() ?? ''
  const dialogueHint = page.dialogueHint?.trim() ?? ''
  return {
    ...page,
    visualPrompt: visualPrompt || buildPageVisualPrompt(summary, storyGoal),
    dialogueHint: dialogueHint || buildPageDialogueHint(summary, storyGoal),
  }
}

function buildPageVisualPrompt(summary: string, storyGoal: string): string {
  if (!summary && !storyGoal) return '围绕当前页核心情节设计画面，保持角色一致、构图清晰。'
  if (!storyGoal) return `围绕「${clipText(summary, 120)}」设计画面，保持角色一致、构图清晰。`
  if (!summary) return `为了「${clipText(storyGoal, 120)}」设计画面，保持角色一致、构图清晰。`
  return `围绕「${clipText(summary, 120)}」设计画面，并突出「${clipText(storyGoal, 120)}」，保持角色一致、构图清晰。`
}

function buildPageDialogueHint(summary: string, storyGoal: string): string {
  if (storyGoal) return `围绕「${clipText(storyGoal, 100)}」提炼本页关键对白，控制在 1-3 句。`
  if (summary) return `围绕「${clipText(summary, 100)}」提炼本页关键对白，控制在 1-3 句。`
  return '提炼本页关键对白，控制在 1-3 句。'
}

function splitPageScript(page: ComicPage, count: number): string[] {
  const primary =
    page.summary?.trim() || page.storyGoal?.trim() || page.visualPrompt?.trim() || ''
  if (!primary) return Array.from({ length: count }, () => '')
  if (count <= 1) return [primary]
  const parts = primary
    .split(/(?<=[。！？!?；;])\s*/)
    .map((part) => part.trim())
    .filter(Boolean)
  const out = parts.length > 0 ? parts.slice(0, count) : [primary]
  while (out.length < count) out.push('')
  return out
}

function clipText(raw: string, max: number): string {
  const value = raw.trim()
  return value.length <= max ? value : value.slice(0, max)
}

/** 将旧数据（仅 panels、无 pages）迁移为分页结构 */
export function ensureEpisodePages(episode: ComicEpisode): ComicEpisode {
  const panels = [...episode.panels].sort((a, b) => a.order - b.order)
  if (!episodeNeedsPageMigration(episode)) {
    const panelById = new Map(panels.map((p) => [p.id, p]))
    const used = new Set<string>()
    const pages: ComicPage[] = episode.pages
      .slice()
      .sort((a, b) => a.index - b.index)
      .map((page, i) => {
        const layout = normalizePageLayout(page.layout)
        const panelIds = page.panelIds.filter((id) => panelById.has(id))
        panelIds.forEach((id) => used.add(id))
        return {
          ...page,
          index: i + 1,
          title: comicPageTitleAtIndex(page, i),
          layout,
          panelIds,
          pageCandidates: page.pageCandidates ?? [],
          selectedPageCandidateId: page.selectedPageCandidateId ?? null,
          pageImageUrl: page.pageImageUrl ?? null,
          pageImageStatus: page.pageImageStatus ?? 'draft',
          pageErrorMessage: page.pageErrorMessage ?? undefined,
          pageCreationTaskId: page.pageCreationTaskId ?? null,
        }
      })
    const orphans = panels.filter((p) => !used.has(p.id))
    const { pages: trimmedPages, spill: overflowPanelIds } = collectOverflowPanelIds(pages)
    const overflowSet = new Set(overflowPanelIds)
    const refillIds = [
      ...overflowPanelIds,
      ...orphans.filter((p) => !overflowSet.has(p.id)).map((p) => p.id),
    ]
    const distributed = distributePanelIdsToPages(trimmedPages, refillIds)
    const normalizedPages = [...distributed.pages]
    if (distributed.spill.length > 0) {
      const spillPanels = distributed.spill
        .map((id) => panelById.get(id))
        .filter((panel): panel is ComicPanel => Boolean(panel))
      normalizedPages.push(...chunkPanelsIntoPages(spillPanels, normalizedPages.length + 1))
    }
    return { ...episode, panels, pages: normalizedPages.length > 0 ? normalizedPages : [createEmptyPage(1)] }
  }

  if (panels.length === 0) {
    return { ...episode, panels, pages: [createEmptyPage(1)] }
  }

  return { ...episode, panels, pages: chunkPanelsIntoPages(panels, 1) }
}

function chunkPanelsIntoPages(panels: ComicPanel[], startIndex: number): ComicPage[] {
  const pages: ComicPage[] = []
  let pageIndex = startIndex
  for (let i = 0; i < panels.length; ) {
    const remaining = panels.length - i
    let layout: ComicPageLayoutTemplate = '2x2'
    let take = 4
    if (remaining === 1) {
      layout = '1x1'
      take = 1
    } else if (remaining === 2) {
      layout = '2x1'
      take = 2
    } else if (remaining === 3) {
      layout = '2x2'
      take = 3
    }
    const chunk = panels.slice(i, i + take)
    pages.push({
      id: randomUuid(),
      index: pageIndex,
      title: `第 ${pageIndex} 页`,
      layout,
      panelIds: chunk.map((p) => p.id),
      pageImageUrl: null,
      selectedPageCandidateId: null,
      pageCandidates: [],
      pageImageStatus: 'draft',
      pageErrorMessage: undefined,
      pageCreationTaskId: null,
    })
    pageIndex += 1
    i += take
  }
  return pages
}

export function getEpisodePage(episode: ComicEpisode, pageId: string | null): ComicPage | null {
  const normalized = ensureEpisodePages(episode)
  if (!pageId) return normalized.pages[0] ?? null
  return normalized.pages.find((p) => p.id === pageId) ?? normalized.pages[0] ?? null
}

export function getPanelsOnPage(episode: ComicEpisode, page: ComicPage): ComicPanel[] {
  const byId = new Map(episode.panels.map((p) => [p.id, p]))
  return page.panelIds.map((id) => byId.get(id)).filter((p): p is ComicPanel => Boolean(p))
}

export function getPanelsInPageOrder(episode: ComicEpisode): ComicPanel[] {
  const normalized = ensureEpisodePages(episode)
  const out: ComicPanel[] = []
  const seen = new Set<string>()
  for (const page of normalized.pages) {
    for (const panel of getPanelsOnPage(normalized, page)) {
      if (!seen.has(panel.id)) {
        seen.add(panel.id)
        out.push(panel)
      }
    }
  }
  for (const p of normalized.panels) {
    if (!seen.has(p.id)) out.push(p)
  }
  return out.sort((a, b) => a.order - b.order)
}

export function findPageContainingPanel(episode: ComicEpisode, panelId: string): ComicPage | null {
  const normalized = ensureEpisodePages(episode)
  return normalized.pages.find((page) => page.panelIds.includes(panelId)) ?? null
}

export function updateEpisodePages(
  episode: ComicEpisode,
  updater: (pages: ComicPage[]) => ComicPage[],
): ComicEpisode {
  const normalized = ensureEpisodePages(episode)
  const pages = reindexComicPages(updater(normalized.pages))
  return { ...normalized, pages }
}

export function pickPageForNewPanel(episode: ComicEpisode, preferredPageId?: string | null): ComicPage {
  const normalized = ensureEpisodePages(episode)
  const preferred = preferredPageId
    ? normalized.pages.find((p) => p.id === preferredPageId)
    : null
  if (preferred && pageHasRoom(preferred.layout, preferred.panelIds.length)) {
    return preferred
  }
  const lastWithRoom = [...normalized.pages]
    .reverse()
    .find((p) => pageHasRoom(p.layout, p.panelIds.length))
  if (lastWithRoom) return lastWithRoom
  return createEmptyPage(normalized.pages.length + 1, COMIC_PAGE_LAYOUT_DEFAULT)
}

export function appendPanelToPageIds(page: ComicPage, panelId: string): ComicPage {
  const max = getPageLayoutOption(page.layout).maxPanels
  if (page.panelIds.length >= max) return page
  return { ...page, panelIds: [...page.panelIds, panelId] }
}

export function removePanelIdFromPages(pages: ComicPage[], panelId: string): ComicPage[] {
  return pages.map((page) => ({
    ...page,
    panelIds: page.panelIds.filter((id) => id !== panelId),
  }))
}

export function reindexPagesAfterPanelDelete(pages: ComicPage[]): ComicPage[] {
  return pages.filter((page) => page.panelIds.length > 0)
}

/** 按分页顺序 + 页内 panelIds 顺序，重排全话分镜的 order（与阅读顺序一致） */
export function renumberEpisodePanelOrders(episode: ComicEpisode): ComicEpisode {
  const normalized = ensureEpisodePages(episode)
  const orderById = new Map<string, number>()
  let n = 1
  for (const page of normalized.pages) {
    for (const id of page.panelIds) {
      if (!orderById.has(id)) orderById.set(id, n++)
    }
  }
  for (const p of normalized.panels) {
    if (!orderById.has(p.id)) orderById.set(p.id, n++)
  }
  return {
    ...normalized,
    panels: normalized.panels.map((p) => ({
      ...p,
      order: orderById.get(p.id) ?? p.order,
    })),
  }
}

/** 仅在当前页内交换相邻分镜（更新 panelIds 顺序并同步 order） */
export function reorderPanelOnPage(
  episode: ComicEpisode,
  pageId: string,
  panelId: string,
  direction: 'up' | 'down',
): ComicEpisode | null {
  const normalized = ensureEpisodePages(episode)
  const page = normalized.pages.find((p) => p.id === pageId)
  if (!page) return null
  const idx = page.panelIds.indexOf(panelId)
  if (idx < 0) return null
  const swapIdx = direction === 'up' ? idx - 1 : idx + 1
  if (swapIdx < 0 || swapIdx >= page.panelIds.length) return null
  const ids = [...page.panelIds]
  ;[ids[idx], ids[swapIdx]] = [ids[swapIdx]!, ids[idx]!]
  const pages = normalized.pages.map((p) => (p.id === pageId ? { ...p, panelIds: ids } : p))
  return renumberEpisodePanelOrders({ ...normalized, pages })
}

/** 将分镜移到目标页的指定槽位（可跨页；先移出再插入并校验版式容量） */
export function movePanelToPageSlot(
  episode: ComicEpisode,
  panelId: string,
  targetPageId: string,
  insertIndex: number,
): ComicEpisode | null {
  const normalized = ensureEpisodePages(episode)
  if (!normalized.pages.some((p) => p.panelIds.includes(panelId))) return null
  const targetPage = normalized.pages.find((p) => p.id === targetPageId)
  if (!targetPage) return null

  const pagesAfterRemove = normalized.pages.map((p) => ({
    ...p,
    panelIds: p.panelIds.filter((id) => id !== panelId),
  }))
  const targetAfterRemove = pagesAfterRemove.find((p) => p.id === targetPageId)
  if (!targetAfterRemove) return null
  const countOnTarget = targetAfterRemove.panelIds.length
  if (!pageHasRoom(targetAfterRemove.layout, countOnTarget)) return null

  const idx = Math.max(0, Math.min(insertIndex, countOnTarget))
  const newIds = [...targetAfterRemove.panelIds]
  newIds.splice(idx, 0, panelId)
  const pages = pagesAfterRemove.map((p) =>
    p.id === targetPageId ? { ...p, panelIds: newIds } : p,
  )
  return renumberEpisodePanelOrders({ ...normalized, pages })
}
